import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { dsoApi } from '../../services/api';
import { FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import Pagination from '../../components/Pagination';

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long'
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).replace(/^(\d+:\d+)(:\d+)?\s*/, '$1 '); // Clean seconds if present
    return `${formattedDate}, ${formattedTime}`;
  } catch (e) {
    return dateString; // Fallback
  }
};

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

function DSOPayouts() {
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter states
  const [statusFilter, setStatusFilter] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalPayouts, setTotalPayouts] = useState(0);
  const [entriesPerPage] = useState(10);

  // Define options for Status filter
  const statusFilterOptions = [
    { value: '', label: 'All Status' },
    { value: 'created', label: 'Created' },
    { value: 'approved', label: 'Approved' },
    { value: 'disbursed', label: 'Disbursed' },
    { value: 'rejected', label: 'Rejected' },
  ];

  // Fetch payouts function
  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const skip = (currentPage - 1) * entriesPerPage;
      const params = {
        skip,
        limit: entriesPerPage,
        ...(statusFilter && { status: statusFilter })
      };
      
      const response = await dsoApi.getPayouts(params);
      setPayouts(response.data.items || []);
      setTotalPayouts(response.data.total || 0);
      setTotalPages(Math.ceil((response.data.total || 0) / entriesPerPage));
    } catch (err) {
      console.error("Error fetching payouts:", err);
      setError('Failed to load payouts. Please try again.');
      setPayouts([]);
      setTotalPayouts(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, entriesPerPage, statusFilter]);

  // Initial fetch and fetch on filter/page change
  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  // Handle page change from Pagination component
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Filter change handlers
  const handleStatusFilterChange = (selectedOption) => {
    // selectedOption will be null if cleared (if isClearable is true), or { value: ..., label: ... }
    setStatusFilter(selectedOption ? selectedOption.value : '');
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Reset filters
  const resetFilters = () => {
    setStatusFilter('');
    setCurrentPage(1);
  };

  // Function to render status badge
  const renderStatusBadge = (status) => {
    let badgeClass = 'dso-status-badge ';
    switch (status?.toLowerCase()) {
      case 'disbursed':
        badgeClass += 'dso-status-verified'; // Green
        break;
      case 'approved':
        badgeClass += 'dso-status-active'; // Blue
        break;
      case 'created':
        badgeClass += 'dso-status-pending'; // Yellow/Orange
        break;
      default:
        badgeClass += 'dso-status-unknown'; // Default style
    }
    return <span className={badgeClass}>{status || 'Unknown'}</span>;
  };

  return (
    <div className="dso-payouts-page dso-list-page">
      <div className="dso-list-header">
        <h2>My Payouts</h2>
      </div>

      {/* Filters */}
      <div className="dso-filters-container">
        <div className="dso-filters-row">
          {/* Status Filter */}
          <div className="dso-filter">
            <label htmlFor="statusFilter">Status:</label>
            <Select
              inputId="statusFilter"
              options={statusFilterOptions}
              value={statusFilterOptions.find(option => option.value === statusFilter)}
              onChange={handleStatusFilterChange}
              isClearable={false}
              placeholder="Select Status..."
              className="dso-react-select-container"
              classNamePrefix="dso-react-select"
              styles={{ container: (base) => ({ ...base, width: '180px' }) }}
            />
          </div>

          <button className="dso-filter-reset" onClick={resetFilters}>
            Reset
          </button>
        </div>
      </div>

      {loading && (
        <div className="dso-loading-state">
          <FaSpinner className="dso-spinner" />
          <span>Loading payouts...</span>
        </div>
      )}

      {error && (
        <div className="dso-error-state">
          <FaExclamationTriangle className="dso-error-icon" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && payouts.length === 0 && (
        <div className="dso-no-data">No payouts found.</div>
      )}

      {!loading && !error && payouts.length > 0 && (
        <>
          <div className="dso-list-table-container">
            <table className="dso-list-table">
              <thead>
                <tr>
                  <th>DSO</th>
                  <th>Amount</th>
                  <th>Cases</th>
                  <th>Status</th>
                  <th>Approved On</th>
                  <th>Disbursed On</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((payout) => (
                  <tr key={payout.id}>
                    <td>{payout.payment_requester_name}</td>
                    <td>{formatCurrency(payout.total_amount)}</td>
                    <td>{payout.case_count || 0}</td>
                    <td>{renderStatusBadge(payout.status_str)}</td>
                    <td>{formatDate(payout.approved_at)}</td>
                    <td>{formatDate(payout.disbursed_at)}</td>
                    <td>
                      <Link 
                        to={`/dso/payouts/${payout.id}/details`}
                        className="dso-action-button"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="dso-pagination-container">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsCount={totalPayouts}
              itemsPerPage={entriesPerPage}
            />
          </div>
        </>
      )}

      <style jsx>{`
        .dso-list-page {
          padding: 15px;
          background-color: #f8fafc;
        }
        
        .dso-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          padding-bottom: 10px;
          border-bottom: 1px solid #e2e8f0;
        }
        

        .dso-list-header h2 {
          margin: 0;
          font-size: 1.3rem;
          font-weight: 600;
          color: #1e293b;
        }
        
        /* Filters */
        .dso-filters-container {
          background: #fff;
          border-radius: 6px;
          padding: 12px;
          margin-bottom: 15px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        
        .dso-filters-row {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          align-items: center;
        }
        
        .dso-filter {
          display: flex;
          align-items: center;
          gap: 5px;
        }
        
        .dso-filter label {
          font-size: 0.8rem;
          font-weight: 500;
          color: #64748b;
          white-space: nowrap;
        }
        
        .dso-filter select {
          padding: 6px 8px;
          border-radius: 4px;
          border: 1px solid #cbd5e1;
          font-size: 0.85rem;
          max-width: 150px;
        }
        
        .dso-filter-reset {
          margin-left: auto;
          padding: 6px 10px;
          border: none;
          background-color: #e2e8f0;
          color: #475569;
          border-radius: 4px;
          font-size: 0.8rem;
          cursor: pointer;
        }
        
        .dso-filter-reset:hover {
          background-color: #cbd5e1;
        }
        
        /* Table */
        .dso-list-table-container {
          background-color: #ffffff;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          overflow-x: auto;
        }
        
        .dso-list-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }
        
        .dso-list-table th {
          text-align: left;
          padding: 10px 12px;
          font-size: 0.75rem;
          font-weight: 600;
          color: #64748b;
          background-color: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
        }
        
        .dso-list-table td {
          padding: 10px 12px;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
          vertical-align: middle;
          white-space: nowrap;
        }
        
        .dso-list-table tr:last-child td {
          border-bottom: none;
        }
        
        .dso-list-table tr:hover {
          background-color: #f8fafc;
        }
        
        /* Status Badges */
        .dso-status-badge {
          display: inline-block;
          padding: 4px 8px;
          font-size: 0.7rem;
          font-weight: 600;
          line-height: 1;
          color: #fff;
          text-align: center;
          white-space: nowrap;
          vertical-align: baseline;
          border-radius: 0.25rem;
          text-transform: capitalize;
        }
        
        .dso-status-verified { background-color: #10b981; color: white; }
        .dso-status-active { background-color: #3b82f6; color: white; }
        .dso-status-pending { background-color: #f59e0b; color: white; }
        .dso-status-unknown { background-color: #94a3b8; color: white; }
        
        /* Agent Link style */
        .dso-agent-link {
          color: #3b82f6;
          text-decoration: none;
          font-weight: 500;
        }
        
        .dso-agent-link:hover {
          text-decoration: underline;
          color: #2563eb;
        }
        
        /* Action button style */
        .dso-action-button {
          display: inline-block;
          padding: 5px 10px;
          background-color: #3b82f6;
          color: white;
          border-radius: 4px;
          font-size: 0.75rem;
          text-decoration: none;
          text-align: center;
          transition: background-color 0.2s;
        }
        
        .dso-action-button:hover {
          background-color: #2563eb;
          color: white;
          text-decoration: none;
        }
        
        /* Loading / Error / No Data States */
        .dso-loading-state,
        .dso-error-state,
        .dso-no-data {
          padding: 30px 0;
          text-align: center;
          color: #64748b;
          font-size: 0.9rem;
          background-color: #fff;
          border-radius: 6px;
          margin-top: 15px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        
        .dso-loading-state,
        .dso-error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }
        
        .dso-spinner {
          font-size: 1.5rem;
          color: #6366f1;
          animation: spin 1s linear infinite;
        }
        
        .dso-error-icon {
          font-size: 1.5rem;
          color: #ef4444;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .dso-pagination-container {
          margin-top: 15px;
        }

        /* Styles for react-select */
        .dso-react-select-container {
          /* Adjust width as needed, or use flex-grow if in a flex container */
          /* width: 200px; */ /* Width is set inline via styles prop for now */
          font-size: 0.85rem; /* Match other inputs */
        }
        /* You can target internal parts using the classNamePrefix */
        .dso-react-select__control {
          min-height: 34px; /* Adjust height to match select */
          height: 34px;
          border: 1px solid #cbd5e1; /* Match select border */
          border-radius: 4px; /* Match select border-radius */
          box-shadow: none; /* Remove default focus shadow if needed */
        }
        .dso-react-select__control--is-focused {
           border-color: #a5b4fc; /* Optional: focus style */
           box-shadow: 0 0 0 1px #a5b4fc;
        }
        .dso-react-select__value-container {
            padding: 0 8px; /* Adjust padding */
            height: 34px;
        }
        .dso-react-select__input-container {
            margin: 0;
            padding: 0;
            height: 32px;
        }
        .dso-react-select__indicators {
            height: 32px;
        }
        .dso-react-select__indicator-separator {
            display: none; /* Hide the separator */
        }
        .dso-react-select__menu {
          z-index: 5; /* Ensure dropdown appears above other elements */
          font-size: 0.85rem;
        }
        .dso-react-select__placeholder {
          color: #94a3b8; /* Lighter placeholder text */
        }
        
        /* Request Payout Button */
        .dso-request-payout-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 8px 16px;
          background-color: #4f46e5;
          color: white;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          text-decoration: none;
          transition: background-color 0.2s;
          border: none;
          cursor: pointer;
        }
        
        .dso-request-payout-button:hover {
          background-color: #4338ca;
          text-decoration: none;
          color: white;
        }
      `}</style>
    </div>
  );
}

export default DSOPayouts;